import type { ApiResponse } from './genesisApi/httpsMethods';

export interface GeocodeAddress {
  asset_name?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  postcode?: string;
  country?: string;
}

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  formatted_address: string;
  address_components: {
    address_1?: string;
    address_2?: string;
    city?: string;
    postcode?: string;
    country?: string;
  };
}

export type GeocodingServiceResponse = ApiResponse<GeocodeResult>;

class GeocodingService {
  private readonly apiKey: string | undefined;

  constructor() {
    this.apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  }

  /**
   * Geocodes an address using Google Maps Geocoding API
   * @param address - Address components to geocode
   * @returns Promise with geocoding result
   */
  async geocodeAddress(address: GeocodeAddress): Promise<GeocodingServiceResponse> {
    if (!this.apiKey) {
      console.warn('Google Maps API key not found. Please add VITE_GOOGLE_MAPS_API_KEY to your environment variables.');
      return {
        success: false,
        status: 500,
        message: 'Google Maps API key not configured'
      };
    }

    // Build address string from components
    const addressParts = [
      address.asset_name,
      address.address_1,
      address.address_2,
      address.city,
      address.postcode,
      address.country
    ].filter(Boolean);

    if (addressParts.length === 0) {
      return {
        success: false,
        status: 400,
        message: 'No address information provided'
      };
    }

    const addressString = addressParts.join(', ');

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addressString)}&key=${this.apiKey}`
      );

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          message: `HTTP error: ${response.status} ${response.statusText}`
        };
      }

      const data = await response.json();

      if (data.status !== 'OK' || !data.results || data.results.length === 0) {
        return {
          success: false,
          status: 422,
          message: data.error_message || `Geocoding failed: ${data.status}`
        };
      }

      const result = data.results[0];
      const location = result.geometry.location;

      // Parse address components
      const addressComponents: any = {};
      
      result.address_components?.forEach((component: any) => {
        const types = component.types;
        
        if (types.includes('street_number')) {
          addressComponents.street_number = component.long_name;
        } else if (types.includes('route')) {
          addressComponents.route = component.long_name;
        } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
          addressComponents.city = component.long_name;
        } else if (types.includes('postal_code')) {
          addressComponents.postcode = component.long_name;
        } else if (types.includes('country')) {
          addressComponents.country = component.long_name;
        } else if (types.includes('subpremise')) {
          addressComponents.subpremise = component.long_name;
        }
      });

      // Construct formatted address components
      const formattedComponents = {
        address_1: [addressComponents.street_number, addressComponents.route].filter(Boolean).join(' ') || address.address_1,
        address_2: addressComponents.subpremise || address.address_2,
        city: addressComponents.city || address.city,
        postcode: addressComponents.postcode || address.postcode,
        country: address.country // Keep original country selection
      };

      return {
        success: true,
        status: 200,
        data: {
          latitude: location.lat,
          longitude: location.lng,
          formatted_address: result.formatted_address,
          address_components: formattedComponents
        }
      };

    } catch (error) {
      console.error('Geocoding error:', error);
      return {
        success: false,
        status: 500,
        message: error instanceof Error ? error.message : 'Unknown geocoding error'
      };
    }
  }

  /**
   * Checks if the geocoding service is properly configured
   * @returns boolean indicating if API key is available
   */
  isConfigured(): boolean {
    return !!this.apiKey;
  }
}

// Export singleton instance
export const geocodingService = new GeocodingService();

// Export the service class for testing or custom instances
export { GeocodingService };
